import { createRouter, useRouter } from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { ErrorState } from "@diverge/ui/components/error-state";
import { Spinner } from "@diverge/ui/components/spinner";

import { routeTree } from "./routeTree.gen";
import { isStaleChunkError } from "./utils/staleChunk";

function RoutePending() {
  return (
    <div className="flex h-full min-h-40 w-full items-center justify-center">
      <Spinner size="lg" aria-label="Loading page" />
    </div>
  );
}

function getRouteErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error || "Unknown error");
}

function RouteError({ error, reset }: ErrorComponentProps) {
  const activeRouter = useRouter();
  // A chunk this tab was built against no longer exists on the server (see
  // utils/staleChunk). Re-rendering can't help — only a reload can.
  const isStaleChunk = isStaleChunkError(error);

  return (
    <div className="flex h-full min-h-80 w-full items-center justify-center p-6">
      <ErrorState
        title="Something went wrong"
        description={
          isStaleChunk
            ? "A new version of the dashboard was released while this tab was open. Reload to continue."
            : getRouteErrorMessage(error)
        }
        retryLabel={isStaleChunk ? "Reload" : "Try again"}
        onRetry={() => {
          if (isStaleChunk) {
            window.location.reload();
            return;
          }
          reset();
          void activeRouter.invalidate();
        }}
        className="w-full max-w-xl"
      />
    </div>
  );
}

export const router = createRouter({
  routeTree,
  // Route chunks start loading on hover/touchstart, so the actual click
  // navigates instantly instead of waiting for the lazy import.
  defaultPreload: "intent",
  defaultPreloadStaleTime: 30_000,
  // Code-split chunks can take a moment (especially dev on-demand
  // transforms): show a pending state quickly so clicks never feel dead.
  defaultPendingComponent: RoutePending,
  defaultErrorComponent: RouteError,
  defaultPendingMs: 100,
  defaultPendingMinMs: 200,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
